/**
 * `useState` remembered in this browser (localStorage) — for per-viewer
 * conveniences like a sort order or a list/grid choice. Storage can be
 * missing or throw (private windows, blocked site data); the value then just
 * isn't remembered.
 */
import { useCallback, useState } from 'react';

export function usePersistedState<T>(
  key: string,
  fallback: T,
  /** Accept a stored value only if it's still a valid one. */
  accept: (value: unknown) => value is T,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const parsed: unknown = JSON.parse(raw);
        if (accept(parsed)) return parsed;
      }
    } catch {
      // unavailable or malformed — use the fallback
    }
    return fallback;
  });
  const set = useCallback(
    (next: T) => {
      setValue(next);
      try {
        window.localStorage.setItem(key, JSON.stringify(next));
      } catch {
        // a convenience only
      }
    },
    [key],
  );
  return [value, set];
}
